import cx from 'classnames'
import {
  type Dispatch,
  type MouseEvent,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
} from 'react'
import { useForm } from 'react-hook-form'

import { type SanityCartSettings } from '@data/sanity/queries/types/cart'
import {
  AnalyticsEventName,
  getInitiateCheckoutEventPayload,
} from '@lib/analytics'
import { AnalyticsContext } from '@lib/analytics-context'
import { CartContext } from '@lib/cart/context'
import { usePrepareCart } from '@lib/cart/hooks'
import { type CartFormValues } from '@lib/cart/types'
import { DiscountContext } from '@lib/discount/context'
import { LanguageContext } from '@lib/language-context'
import { type ErrorMessages } from '@lib/request'
import { ShopContext } from '@lib/shop-context'
import { formatCheckoutUrl } from '@lib/shopify/checkout'
import { StringsContext } from '@lib/strings-context'
import { UserContext } from '@lib/user/context'

import Button from '@components/buttons/button'
import Checkbox from '@components/checkbox'
import SimplePortableText from '@components/simple-portable-text'

interface CartSubmitProps {
  cartSettings: SanityCartSettings
  cartFormValues: CartFormValues
  setErrorMessages: Dispatch<SetStateAction<ErrorMessages>>
  onClick?: () => void
  children: ReactNode
  className?: string
}

const CartSubmit = ({
  cartSettings,
  cartFormValues,
  setErrorMessages,
  onClick,
  children,
  className,
}: CartSubmitProps) => {
  const { triggerAnalyticsEvent } = useContext(AnalyticsContext)
  const { cart, isCartUpdating, isCartSubmitting, setIsCartSubmitting } =
    useContext(CartContext)
  const { cartDiscountItems } = useContext(DiscountContext)
  const { locale } = useContext(LanguageContext)
  const { currencyCode } = useContext(ShopContext)
  const strings = useContext(StringsContext)
  const { user } = useContext(UserContext)

  const { register, watch } = useForm()
  const prepareCart = usePrepareCart()

  const acceptTermsRegister = register('acceptTerms')
  const acceptTerms = watch('acceptTerms')

  const successRedirect = useCallback(async () => {
    // Redirect to checkout
    const formattedCheckoutUrl = await formatCheckoutUrl(
      cart.webUrl,
      locale,
      cartSettings.storeUrl,
    )
    window.location.href = formattedCheckoutUrl
  }, [cart, cartSettings, locale])

  const handleClick = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()

      try {
        setIsCartSubmitting(true)

        const prepareCartResult = await prepareCart(cartFormValues)

        setErrorMessages((currentErrorMessages) => ({
          ...currentErrorMessages,
          vatId: prepareCartResult.errors.vatId,
        }))

        if (Object.entries(prepareCartResult.errors).length > 0) {
          console.log(prepareCartResult.errors)
          setIsCartSubmitting(false)
          return
        }

        // Trigger on click handler
        if (onClick) {
          onClick()
        }

        setIsCartSubmitting(false)

        setTimeout(async () => {
          // Trigger initiate checkout event
          const eventPayload = getInitiateCheckoutEventPayload(
            cart,
            currencyCode,
            cartDiscountItems,
          )
          triggerAnalyticsEvent(
            AnalyticsEventName.InitiateCheckout,
            eventPayload,
          )

          await successRedirect()
        }, 0)
      } catch (error) {
        console.log(error)

        setIsCartSubmitting(false)
      }
    },
    [
      cart,
      cartDiscountItems,
      cartFormValues,
      currencyCode,
      onClick,
      prepareCart,
      setErrorMessages,
      setIsCartSubmitting,
      successRedirect,
      triggerAnalyticsEvent,
    ],
  )

  const isDisabled =
    !user ||
    isCartUpdating ||
    isCartSubmitting ||
    (!!cartSettings.terms && !acceptTerms)

  return (
    <div className={cx('space-y-4', className)}>
      {!!cartSettings.terms && (
        <Checkbox formRegister={acceptTermsRegister}>
          <SimplePortableText content={cartSettings.terms} className="rc" />
        </Checkbox>
      )}

      <Button
        variant="filled"
        disabled={isDisabled}
        onClick={handleClick}
        className="w-full"
      >
        {isCartUpdating && <>{strings.buttonUpdating}</>}
        {!isCartUpdating && isCartSubmitting && <>{strings.buttonSubmitting}</>}
        {!isCartUpdating && !isCartSubmitting && <>{children}</>}
      </Button>
    </div>
  )
}

export default CartSubmit
